import { Inject, Injectable, InjectionToken, signal } from '@angular/core';
import { environment } from '../../environments/environment';
import * as msal from '@azure/msal-browser';
import { DatasService } from './datas.service';
import { CurrentService } from './current.service';
import { Router } from '@angular/router';
import { CustomerService } from './customer.service';
import { HbmIframeService } from './hbm/hbm-iframe.service';
import { ModalService, MODAL } from './modal.service';
import { GTM } from './gtm.service';
import { KingfisherService } from './kingfisher/kingfisher.service';
import { SearchInput } from './kingfisher/search-input';
import { LiteMarketingChannel, PagedCustomerResponse } from './kingfisher/customer';
import { jwtDecode } from 'jwt-decode';
import { Store, Vendor } from '../models/vendor';
import { logError } from './helpers/sentry.helper';
import { HttpMethod, RestService } from './rest.service';
import { firstValueFrom } from 'rxjs';

export type MsalApplicationFactory = (configuration: msal.Configuration) => msal.IPublicClientApplication;

export const MSAL_APPLICATION_FACTORY = new InjectionToken<MsalApplicationFactory>('MSAL_APPLICATION_FACTORY', {
  providedIn: 'root',
  factory: () => (configuration: msal.Configuration) => new msal.PublicClientApplication(configuration),
});

export type SsoReauthenticationState = {
  visible: boolean;
  busy: boolean;
  errorKey?: string;
};

type PendingTokenRecovery = {
  account: msal.AccountInfo;
  sessionVersion: number;
  promise: Promise<string>;
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
  popupPromise?: Promise<void>;
};

const HIDDEN_REAUTHENTICATION_STATE: SsoReauthenticationState = {
  visible: false,
  busy: false,
};

const SSO_REAUTHENTICATION_ERROR_KEYS = {
  cancelled: 'popins.sso-reauthentication.cancelled',
  popupBlocked: 'popins.sso-reauthentication.popup-blocked',
  failed: 'popins.sso-reauthentication.failed',
} as const;

@Injectable({
  providedIn: 'root',
})
export class VendorService extends RestService {
  public isLogged = false;
  private readonly authenticationReadyState = signal(false);
  public readonly authenticationReady = this.authenticationReadyState.asReadonly();
  private readonly authenticationInitializingState = signal(false);
  public readonly authenticationInitializing = this.authenticationInitializingState.asReadonly();
  private readonly authenticationInitializationFailedState = signal(false);
  public readonly authenticationInitializationFailed = this.authenticationInitializationFailedState.asReadonly();
  private readonly reauthenticationState = signal<SsoReauthenticationState>(HIDDEN_REAUTHENTICATION_STATE);
  public readonly reauthentication = this.reauthenticationState.asReadonly();

  private msalInstance?: msal.IPublicClientApplication;
  private msalInitializationPromise?: Promise<void>;
  private loginPromise?: Promise<void>;
  private interactiveRequest?: { key: string; promise: Promise<unknown> };
  private accessTokenRequest?: { key: string; promise: Promise<string> };
  private pendingTokenRecovery?: PendingTokenRecovery;
  private sessionVersion = 0;
  private scopes: string[] = [];

  constructor(
    private readonly byMe: HbmIframeService,
    private readonly datas: DatasService,
    private readonly current: CurrentService,
    private readonly router: Router,
    private readonly customer: CustomerService,
    private readonly gtm: GTM,
    private readonly modal: ModalService,
    private readonly kfService: KingfisherService,
    @Inject(MSAL_APPLICATION_FACTORY) private readonly msalApplicationFactory: MsalApplicationFactory
  ) {
    super();
    // MSAL est préparé dès l'affichage de l'accueil. Le clic de connexion peut ainsi
    // ouvrir directement la popup, sans acquisition silencieuse préalable.
    void this.initMSAL().catch(err => {
      logError('[SSO] Initialisation anticipée de MSAL impossible', err);
    });
  }

  private initMSAL(): Promise<void> {
    if (this.msalInstance) {
      return Promise.resolve();
    }

    if (!this.msalInitializationPromise) {
      this.authenticationInitializingState.set(true);
      this.authenticationInitializationFailedState.set(false);
      this.msalInitializationPromise = this.createAndInitializeMsal().catch(err => {
        this.msalInstance = undefined;
        this.scopes = [];
        this.authenticationReadyState.set(false);
        this.authenticationInitializingState.set(false);
        this.authenticationInitializationFailedState.set(true);
        this.msalInitializationPromise = undefined;
        throw err;
      });
    }

    return this.msalInitializationPromise;
  }

  private async createAndInitializeMsal(): Promise<void> {
    const msalConfig = {
      auth: environment.castorama.msalAuthConf,
      cache: {
        cacheLocation: 'localStorage',
        storeAuthStateInCookie: false,
        claimsBasedCachingEnabled: true,
      },
    } as msal.Configuration;

    const msalInstance = this.msalApplicationFactory(msalConfig);
    await msalInstance.initialize();

    this.scopes = [`${msalInstance.getConfiguration().auth.clientId}/.default`];
    this.msalInstance = msalInstance;
    this.authenticationReadyState.set(true);
    this.authenticationInitializingState.set(false);
    this.authenticationInitializationFailedState.set(false);
  }

  prepareAuthentication(): Promise<void> {
    return this.initMSAL();
  }

  getAccessToken(): Promise<string> {
    return this.initMSAL().then(() => {
      const msalInstance = this.getInitializedMsal();
      const account = msalInstance.getActiveAccount();

      if (!account) {
        throw new Error('[SSO] Aucun compte actif pour récupérer le token');
      }

      const sessionVersion = this.sessionVersion;
      const requestKey = `${sessionVersion}:${account.homeAccountId}`;

      if (this.accessTokenRequest?.key === requestKey) {
        return this.accessTokenRequest.promise;
      }

      const tokenPromise = this.acquireAccessToken(account, sessionVersion);
      const trackedPromise = tokenPromise.finally(() => {
        if (this.accessTokenRequest?.promise === trackedPromise) {
          this.accessTokenRequest = undefined;
        }
      });

      this.accessTokenRequest = {
        key: requestKey,
        promise: trackedPromise,
      };

      return trackedPromise;
    });
  }

  reconnectFromUserGesture(): Promise<void> {
    const recovery = this.pendingTokenRecovery;

    if (!recovery) {
      return Promise.resolve();
    }

    if (recovery.popupPromise) {
      return recovery.popupPromise;
    }

    this.reauthenticationState.set({
      visible: true,
      busy: true,
    });

    let popupResult: Promise<msal.AuthenticationResult>;

    try {
      // Aucun await ne doit précéder cet appel : acquireTokenPopup est ainsi
      // directement rattaché au clic utilisateur et n'est pas bloqué par le navigateur.
      popupResult = this.runInteractive(`token:${recovery.account.homeAccountId}`, () =>
        this.getInitializedMsal().acquireTokenPopup({
          scopes: this.scopes,
          account: recovery.account,
        })
      );
    } catch (err) {
      this.recordReauthenticationFailure(recovery, err);
      return Promise.resolve();
    }

    const completion = this.completeReauthentication(recovery, popupResult);
    recovery.popupPromise = completion;
    return completion;
  }

  async logout(): Promise<void> {
    this.sessionVersion++;
    this.rejectPendingTokenRecovery(new Error('[SSO] Acquisition du token interrompue par la déconnexion'));
    const msalInstance = this.msalInstance;
    const account = msalInstance?.getActiveAccount() ?? null;

    try {
      if (msalInstance) {
        await this.runInteractive(`logout:${account?.homeAccountId ?? 'all'}`, () =>
          account ? msalInstance.logoutPopup({ account }) : msalInstance.logoutPopup()
        );
      }
    } finally {
      msalInstance?.setActiveAccount(null);
      this.clearLocalSession();
    }
  }

  login(callback?: () => void): Promise<void> {
    if (!this.loginPromise) {
      const sessionVersion = this.sessionVersion;
      // Dans le cas normal, l'initialisation anticipée est déjà terminée :
      // loginPopup est alors invoquée synchroniquement depuis le clic.
      const authentication = this.msalInstance ? this.resolveAuthentication() : undefined;
      this.loginPromise = this.executeLogin(callback, sessionVersion, authentication);
    }

    return this.loginPromise;
  }

  private async executeLogin(
    callback: (() => void) | undefined,
    sessionVersion: number,
    authentication?: Promise<msal.AuthenticationResult>
  ): Promise<void> {
    try {
      await this.performLogin(callback, sessionVersion, authentication);
    } finally {
      this.loginPromise = undefined;
    }
  }

  private async performLogin(
    callback: (() => void) | undefined,
    sessionVersion: number,
    authentication?: Promise<msal.AuthenticationResult>
  ): Promise<void> {
    try {
      console.info('[SSO] Initialisation de MSAL');
      await this.initMSAL();

      if (sessionVersion !== this.sessionVersion) {
        return;
      }

      console.info('[SSO] Ouverture de la popup Microsoft depuis l’action utilisateur');
      const authResult = await (authentication ?? this.resolveAuthentication());

      if (sessionVersion !== this.sessionVersion) {
        return;
      }

      if (!authResult?.account) {
        logError('[SSO] Aucun compte Microsoft sélectionné');
        return;
      }

      this.setActiveAccount(authResult.account);

      console.info('[SSO] Sélection du magasin');
      await this.selectStore(authResult.account, callback, authResult.accessToken);
    } catch (err) {
      if (this.getMsalErrorCode(err) === 'user_cancelled') {
        console.log("[SSO] L'utilisateur a annulé la connexion");
      } else {
        logError('[SSO] login() => ERROR', JSON.stringify(err, Object.getOwnPropertyNames(err)));
      }

      throw err;
    }
  }

  private resolveAuthentication(): Promise<msal.AuthenticationResult> {
    const msalInstance = this.getInitializedMsal();
    return this.runInteractive('login', () =>
      msalInstance.loginPopup({
        scopes: this.scopes,
        prompt: 'select_account',
      })
    );
  }

  private async selectStore(account: msal.AccountInfo, callback?: () => void, accessToken?: string): Promise<void> {
    const idTokenClaims = account.idTokenClaims as Record<string, unknown> | undefined;
    let _mustSelectStore: boolean = false;

    if (!idTokenClaims?.SapStoreId) _mustSelectStore = true;
    if (!idTokenClaims?.SapId) _mustSelectStore = true;
    if (!environment.production) _mustSelectStore = true;

    // Si utilisateur non-vendeur, on peut avoir un SapStoreId qui ne correspond pas à un magasin
    if (!_mustSelectStore) {
      let _attachedStore = this.datas.storeList.find(s => s.SAPStoreId == idTokenClaims.SapStoreId);
      if (!_attachedStore) _mustSelectStore = true;
    }

    if (_mustSelectStore) {
      this.modal.display(MODAL.SELECT_STORE, {
        storeList: this.datas.storeList,
        onComplete: async selectedStore => await this.postLogin(selectedStore, account, callback, accessToken),
      });
    } else {
      let _store = this.datas.storeList.find(s => s.SAPStoreId == idTokenClaims.SapStoreId);
      await this.postLogin(_store, account, callback, accessToken);
    }
  }

  private async postLogin(
    store: Store,
    account: msal.AccountInfo,
    callback?: () => void,
    accessToken?: string
  ): Promise<void> {
    if (!store) {
      logError('[SSO] Aucun magasin sélectionné');
      return;
    }

    if (!this.isActiveAccount(account)) {
      logError('[SSO] Post-login ignoré car le compte actif a changé');
      return;
    }

    let token = accessToken;

    if (!token) {
      try {
        token = await this.getAccessToken();
      } catch (err) {
        logError(err);
        return;
      }
    }

    let decodedMsalToken: { given_name?: any; family_name?: any } = {};

    try {
      decodedMsalToken = jwtDecode(token);
    } catch (err) {
      logError(err);
    }

    const idTokenClaims = account.idTokenClaims as any;
    // Les claims de l'ID token validé par MSAL restent prioritaires sur le décodage de l'access token.
    const tokenClaims = { ...decodedMsalToken, ...idTokenClaims };
    const sapId = (tokenClaims.SapId || tokenClaims.sapid || tokenClaims.sap_id) as string;

    const vendor: Vendor = {
      name: account.name,
      sapId: sapId || 'No-SapId',
      store: store,
      email: account.username,
      givenName: this.capitalizeName(tokenClaims.given_name || tokenClaims.givenName || tokenClaims.GivenName) ?? '',
      familyName: (tokenClaims.family_name || tokenClaims.familyName || tokenClaims.FamilyName)?.toUpperCase() ?? '',
      accessToken: () => this.getAccessToken(),
      orgUnitText: (tokenClaims.OrgUnitText as string) || (tokenClaims.orgUnitText as string) || undefined,
    };
    this.current.vendor = this.applyVendorOverrides(vendor);

    this.gtm.trackEvent('se-connecter');

    try {
      await this.syncMe(token);
      await this.byMe.getTokenLegal();
      await this.datas.enableApplications(this.current.vendor.sapId, store.catalogs);
      this.isLogged = true;
      callback?.();
    } catch (err) {
      this.current.vendor = null;
      this.isLogged = false;
      logError('[SSO] postLogin => ERROR', JSON.stringify(err, Object.getOwnPropertyNames(err)));
      throw err;
    }
  }

  async searchCustomers(searchInputs: SearchInput): Promise<PagedCustomerResponse> {
    // Recherche des clients
    try {
      const token = await this.getAccessToken();
      return await this.kfService.searchCustomers(searchInputs, token);
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  async fetchMarketingConsentData(kfId: string): Promise<LiteMarketingChannel[]> {
    try {
      const token = await this.getAccessToken();
      return (await this.kfService.fetchMarketingConsentData(kfId, token)).marketingConsent;
    } catch (error) {
      logError(error);
      throw error;
    }
  }

  private capitalizeName(name: string): string | undefined {
    if (!name) return undefined;

    try {
      return name
        .toLowerCase()
        .split(' ')
        .map(part =>
          part
            .split('-')
            .map(subPart => subPart.charAt(0).toUpperCase() + subPart.slice(1))
            .join('-')
        )
        .join(' ');
    } catch (err) {
      console.error(err);
      return name;
    }
  }

  private applyVendorOverrides(vendor: Vendor): Vendor {
    const debugConfig = environment.configurators2d.global.debug as {
      vendorOverrides?: Partial<Pick<Vendor, 'orgUnitText'>>;
    };

    if (!debugConfig.vendorOverrides) {
      return vendor;
    }

    if (
      debugConfig.vendorOverrides.orgUnitText !== undefined &&
      debugConfig.vendorOverrides.orgUnitText !== vendor.orgUnitText
    ) {
      console.info('[SSO] Override vendeur appliqué', {
        property: 'orgUnitText',
        from: vendor.orgUnitText,
        to: debugConfig.vendorOverrides.orgUnitText,
      });
    }

    return {
      ...vendor,
      orgUnitText: debugConfig.vendorOverrides.orgUnitText ?? vendor.orgUnitText,
    };
  }

  async syncMe(accessToken?: string): Promise<void> {
    const vendor = this.current.vendor;

    const buildHeaders = async (): Promise<{ [header: string]: string }> => {
      const token = accessToken ?? (await this.getAccessToken());
      return {
        'Content-Type': 'application/json',
        'X-Vendor-StoreId': vendor?.store?.SAPStoreId ?? '',
        'X-Vendor-LegacyStoreId': vendor?.store?.legacyStoreId ?? '',
        Authorization: `Bearer ${token}`,
      };
    };

    return firstValueFrom(
      this.restApi(
        {
          method: HttpMethod.POST,
          url: `${environment.configurators2d.baseApi}vendors/me/sync`,
          body: {},
          withCredentials: true,
        },
        await buildHeaders()
      )
    );
  }

  private getInitializedMsal(): msal.IPublicClientApplication {
    if (!this.msalInstance) {
      throw new Error("[SSO] MSAL n'est pas initialisé");
    }

    return this.msalInstance;
  }

  private setActiveAccount(account: msal.AccountInfo | null): void {
    this.getInitializedMsal().setActiveAccount(account);
  }

  private isActiveAccount(account: msal.AccountInfo): boolean {
    const activeAccount = this.getInitializedMsal().getActiveAccount();
    return activeAccount?.homeAccountId === account.homeAccountId;
  }

  private requireSameAccount(actual: msal.AccountInfo | null, expected: msal.AccountInfo): void {
    if (!actual || actual.homeAccountId !== expected.homeAccountId) {
      throw new Error('[SSO] MSAL a retourné un token pour un compte inattendu');
    }
  }

  private requireAccessToken(authResult: msal.AuthenticationResult, expectedAccount: msal.AccountInfo): string {
    this.requireSameAccount(authResult.account, expectedAccount);

    if (!authResult.accessToken) {
      throw new Error("[SSO] MSAL n'a retourné aucun access token");
    }

    return authResult.accessToken;
  }

  private async acquireAccessToken(account: msal.AccountInfo, sessionVersion: number): Promise<string> {
    try {
      const authResult = await this.getInitializedMsal().acquireTokenSilent({
        scopes: this.scopes,
        account,
      });
      return this.requireAccessToken(authResult, account);
    } catch (silentError) {
      console.warn('[SSO] Acquisition silencieuse du token indisponible : reconnexion utilisateur requise', {
        msalErrorCode: this.getMsalErrorCode(silentError) ?? 'unknown',
      });

      if (!this.isLogged || sessionVersion !== this.sessionVersion) {
        throw silentError;
      }

      return this.waitForReauthentication(account, sessionVersion);
    }
  }

  private waitForReauthentication(account: msal.AccountInfo, sessionVersion: number): Promise<string> {
    const currentRecovery = this.pendingTokenRecovery;

    if (currentRecovery) {
      if (
        currentRecovery.account.homeAccountId === account.homeAccountId &&
        currentRecovery.sessionVersion === sessionVersion
      ) {
        return currentRecovery.promise;
      }

      throw new Error('[SSO] Une reconnexion concerne déjà une autre session Microsoft');
    }

    let resolve!: (token: string) => void;
    let reject!: (error: unknown) => void;
    const promise = new Promise<string>((promiseResolve, promiseReject) => {
      resolve = promiseResolve;
      reject = promiseReject;
    });

    this.pendingTokenRecovery = {
      account,
      sessionVersion,
      promise,
      resolve,
      reject,
    };
    this.reauthenticationState.set({
      visible: true,
      busy: false,
    });

    return promise;
  }

  private async completeReauthentication(
    recovery: PendingTokenRecovery,
    popupResult: Promise<msal.AuthenticationResult>
  ): Promise<void> {
    try {
      const authResult = await popupResult;

      if (this.pendingTokenRecovery !== recovery || recovery.sessionVersion !== this.sessionVersion) {
        return;
      }

      const token = this.requireAccessToken(authResult, recovery.account);
      this.setActiveAccount(authResult.account);
      this.pendingTokenRecovery = undefined;
      this.reauthenticationState.set(HIDDEN_REAUTHENTICATION_STATE);
      recovery.resolve(token);
    } catch (err) {
      this.recordReauthenticationFailure(recovery, err);
    }
  }

  private recordReauthenticationFailure(recovery: PendingTokenRecovery, err: unknown): void {
    const errorCode = this.getMsalErrorCode(err);

    if (errorCode === 'user_cancelled') {
      console.info('[SSO] L’utilisateur a annulé la reconnexion Microsoft');
    } else {
      logError(err);
    }

    if (this.pendingTokenRecovery !== recovery) {
      return;
    }

    recovery.popupPromise = undefined;
    this.reauthenticationState.set({
      visible: true,
      busy: false,
      errorKey: this.getReauthenticationErrorKey(errorCode),
    });
  }

  private getReauthenticationErrorKey(errorCode: string | undefined): string {
    switch (errorCode) {
      case 'user_cancelled':
        return SSO_REAUTHENTICATION_ERROR_KEYS.cancelled;
      case 'popup_window_error':
        return SSO_REAUTHENTICATION_ERROR_KEYS.popupBlocked;
      default:
        return SSO_REAUTHENTICATION_ERROR_KEYS.failed;
    }
  }

  private rejectPendingTokenRecovery(err: unknown): void {
    const recovery = this.pendingTokenRecovery;
    this.pendingTokenRecovery = undefined;
    this.reauthenticationState.set(HIDDEN_REAUTHENTICATION_STATE);
    recovery?.reject(err);
  }

  private runInteractive<T>(key: string, request: () => Promise<T>): Promise<T> {
    if (this.interactiveRequest) {
      if (this.interactiveRequest.key === key) {
        return this.interactiveRequest.promise as Promise<T>;
      }

      throw new Error('[SSO] Une autre interaction Microsoft est déjà en cours');
    }

    const requestPromise = request();
    const trackedPromise = requestPromise.finally(() => {
      if (this.interactiveRequest?.promise === trackedPromise) {
        this.interactiveRequest = undefined;
      }
    });
    this.interactiveRequest = { key, promise: trackedPromise };

    return trackedPromise;
  }

  private getMsalErrorCode(err: unknown): string | undefined {
    if (!err || typeof err !== 'object') {
      return undefined;
    }

    const msalError = err as { errorCode?: unknown; error?: unknown };
    const errorCode = msalError.errorCode ?? msalError.error;
    return typeof errorCode === 'string' ? errorCode : undefined;
  }

  private clearLocalSession(): void {
    this.current.vendor = null;
    this.isLogged = false;
    this.customer.logout();
    void this.router.navigate(['/']);
  }
}
